import { Platform } from 'react-native';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import {
  appLogout,
  createSlice,
  createAction,
  createSelector,
  persistReducer,
  createAsyncThunk,
  AuthenticationStatus,
  PayloadAction,
  injectSlice,
} from '@bonanza-social/toolkit/state';
import GamesService from '../../services/GamesService/GamesService';
import { Game, GamesResponse, LaunchGamePayload } from '../shared/games.types';
import { GamesState, LastLaunchedGame } from './games.types';
import { generateUrlName } from '../../utils/games';
import { TOP_SLOT_PROVIDERS } from '../../components/Search/TopSlotProviders/TopSlotProviders.content';
import { getLockedLabel } from '../../components/Games/Game/Game.utils';
import FavoritesService from '../../services/FavoritesService/FavoritesService';

export const fetchGames = createAsyncThunk<GamesResponse, AuthenticationStatus>(
  'games/fetch',
  async (authStatus) =>
    authStatus === 'authenticated'
      ? await GamesService.getGames()
      : await GamesService.getPublicGames(),
);

export const favoriteGame = createAsyncThunk(
  'games/favorite',
  async (gameId: Game['id']) => {
    await FavoritesService.favorite({ pathParams: { gameId } });
  },
);

export const unfavoriteGame = createAsyncThunk(
  'games/unfavorite',
  async (gameId: Game['id']) => {
    await FavoritesService.unfavorite({ pathParams: { gameId } });
  },
);

export const launchGame = createAction<LaunchGamePayload>('games/launch');

const initialState: GamesState = {
  authenticated: {
    ids: [],
    byId: {},
    dataState: 'idle',
  },
  unauthenticated: {
    ids: [],
    byId: {},
    dataState: 'idle',
  },
  lastLaunchedGame: {},
  showLastGameLaunchedToast: false,
};

const games = createSlice({
  name: 'games',
  initialState,
  reducers: {
    setLastLaunchedGame(state, action: PayloadAction<LastLaunchedGame>) {
      state.lastLaunchedGame = { ...state.lastLaunchedGame, ...action.payload };
    },
    setShowLastGameLaunchedToast(state, action: PayloadAction<boolean>) {
      state.showLastGameLaunchedToast = action.payload;
    },
    updateMaintenance(
      state,
      action: PayloadAction<{
        authStatus: 'authenticated' | 'unauthenticated';
        gameIds: Game['id'][];
        isUnderMaintenance: boolean;
      }>,
    ) {
      const { authStatus, gameIds, isUnderMaintenance } = action.payload;

      gameIds.forEach((gameId) => {
        const game = state[authStatus].byId[gameId];
        if (!game) return;

        if (!game.displayFeatures) {
          game.displayFeatures = {};
        }

        if (isUnderMaintenance) {
          game.displayFeatures.IsUnderMaintenance = {};
        } else {
          delete game.displayFeatures.IsUnderMaintenance;
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGames.fulfilled, (state, { meta, payload }) => {
      const { games } = payload;
      state[meta.arg].dataState = 'fulfilled';
      state[meta.arg].ids = Object.keys(games);
      state[meta.arg].byId = games;
    });
    builder.addCase(fetchGames.pending, (state, { meta }) => {
      state[meta.arg].dataState = 'pending';
    });
    builder.addCase(fetchGames.rejected, (state, { meta }) => {
      state[meta.arg].dataState = 'rejected';
    });
    builder.addCase(favoriteGame.pending, (state, action) => {
      const gameId = action.meta.arg;
      state.authenticated.byId[gameId].displayFeatures.Favorite = {};
    });
    builder.addCase(unfavoriteGame.pending, (state, action) => {
      const gameId = action.meta.arg;
      delete state.authenticated.byId[gameId].displayFeatures.Favorite;
    });
    builder.addCase(favoriteGame.rejected, (state, action) => {
      // reset favorite status when service call fails
      const gameId = action.meta.arg;
      delete state.authenticated.byId[gameId].displayFeatures.Favorite;
    });
    builder.addCase(unfavoriteGame.rejected, (state, action) => {
      // reset favorite status when service call fails
      const gameId = action.meta.arg;
      state.authenticated.byId[gameId].displayFeatures.Favorite = {};
    });
    builder.addCase(appLogout, (state) => {
      const { lastLaunchedGame, showLastGameLaunchedToast } = state;
      return {
        authenticated: {
          ...initialState.authenticated,
        },
        unauthenticated: {
          ...initialState.unauthenticated,
        },
        lastLaunchedGame,
        showLastGameLaunchedToast,
      };
    });
  },
});

const persistConfig = {
  key: 'games',
  storage: AsyncStorage,
  whitelist:
    Platform.OS === 'web'
      ? ['lastLaunchedGameId', 'showLastGameLaunchedToast']
      : ['authenticated', 'lastLaunchedGameId', 'showLastGameLaunchedToast'],
  version: 2,
};

const persistedReducer = persistReducer(persistConfig, games.reducer);

export const withGames = injectSlice({
  reducerPath: 'games',
  reducer: persistedReducer,
});

export const {
  setLastLaunchedGame,
  setShowLastGameLaunchedToast,
  updateMaintenance,
} = games.actions;

export const selectGame = (id: Game['id']) =>
  withGames.selector(
    (state) => state.games[state.app.authenticationStatus].byId[id],
  );

export const selectGames = withGames.selector(
  (state) => state.games[state.app.authenticationStatus].byId,
);

export const selectGameIds = withGames.selector(
  (state) => state.games[state.app.authenticationStatus].ids,
);

export const selectGameIdByName = createSelector(
  [(_, name: Game['name']) => name, selectGameIds, selectGames],
  (name, gameIds, games) =>
    gameIds.find(
      (id) =>
        games[id].name.toLowerCase() === name.toLowerCase() ||
        name === generateUrlName(games[id].name),
    ),
);

export const selectCoinGameIds = createSelector(
  [selectGameIds, selectGames],
  (gameIds, games) =>
    gameIds.filter((gameId) => {
      const game = games[gameId];
      return game && game.hasCoinsGameplay;
    }),
);

export const selectGamesState = withGames.selector(
  (state) => state.games[state.app.authenticationStatus].dataState,
);

export const selectLastLaunchedGame = withGames.selector(
  ({ games: { lastLaunchedGame }, account }) =>
    lastLaunchedGame[account?.data?.accountId ?? ''] ||
    initialState.lastLaunchedGame,
);

export const selectShowLastGameLaunchedToast = withGames.selector(
  (state) => state.games.showLastGameLaunchedToast,
);

export const selectSubProviderById = (id: number) => {
  return TOP_SLOT_PROVIDERS[id as keyof typeof TOP_SLOT_PROVIDERS];
};

export const selectProviderGames = createSelector(
  [(_, provider: string) => provider, selectGames, selectGameIds],
  (provider, games, gameIds) =>
    gameIds.filter((id) => games[id].subprovider === provider),
);

export const selectGameReleaseDate = withGames.selector(
  (state, id: Game['id']) =>
    selectGame(id)(state)?.displayFeatures.ComingSoon?.validUntil,
);

export const selectGameIsUnderMaintenance = withGames.selector(
  (state, id: Game['id']) =>
    selectGame(id)(state)?.displayFeatures.IsUnderMaintenance,
);

export const selectAvailableGames = createSelector(
  [selectGameIds, selectGames],
  (gameIds, games) =>
    gameIds.reduce((acc: Game[], gameId) => {
      const game = games[gameId];
      if (game) {
        const releaseDate = game.displayFeatures.ComingSoon?.validUntil;
        const lockedLabel = getLockedLabel(releaseDate);
        if (!lockedLabel) {
          acc.push(game);
        }
      }
      return acc;
    }, []),
);

declare global {
  interface LazyLoadedSlices {
    games: ReturnType<typeof persistedReducer>;
  }
}
